import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, placeholder = "选择日期", className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value || new Date());
  const [view, setView] = useState<'date' | 'time'>('date');
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Update position when opening
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Default: below the trigger
      let top = rect.bottom + scrollY + 8;
      let left = rect.left + scrollX;
      
      // Check if it goes off screen (bottom)
      if (rect.bottom + 350 > window.innerHeight) {
          top = rect.top + scrollY - 360; // Show above
      }
      
      // Check if it goes off screen (right)
      if (rect.left + 280 > window.innerWidth) {
          left = window.innerWidth - 290;
      }

      setPosition({ top, left });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is inside the portal content
      const portalContent = document.getElementById('datepicker-portal-content');
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && 
          portalContent && !portalContent.contains(event.target as Node)) {
        setIsOpen(false);
        setView('date');
      }
    };
    
    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        // Only close if window resizes, but don't close on scroll to allow internal scrolling
        // If the main window scrolls, the popup might detach, but users can close by clicking outside.
        // Or we can attach a scroll listener to update position, but that's complex.
        // Let's remove the aggressive scroll listener for now.
        window.addEventListener('resize', () => setIsOpen(false));
    }
    
    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', () => setIsOpen(false));
    };
  }, [isOpen]);

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    // Preserve time if value exists, otherwise set to current time or 09:00
    if (value) {
        newDate.setHours(value.getHours());
        newDate.setMinutes(value.getMinutes());
        newDate.setSeconds(0);
    } else {
        const now = new Date();
        newDate.setHours(now.getHours());
        newDate.setMinutes(now.getMinutes());
        newDate.setSeconds(0);
    }
    onChange(newDate);
  };

  const handleTimeSelect = (type: 'hour' | 'minute', val: number) => {
      if (!value) return;
      const newDate = new Date(value);
      if (type === 'hour') newDate.setHours(val);
      else newDate.setMinutes(val);
      onChange(newDate);
  };

  const renderCalendar = () => {
    const days = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);
    const blanks = Array(firstDay).fill(null);
    const dayArray = Array.from({ length: days }, (_, i) => i + 1);

    return (
      <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
        {['日', '一', '二', '三', '四', '五', '六'].map(d => (
            <div key={d} className="text-slate-400 text-xs py-1">{d}</div>
        ))}
        {blanks.map((_, i) => (
            <div key={`blank-${i}`} className="h-8" />
        ))}
        {dayArray.map(day => {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const isSelected = value && date.toDateString() === value.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();
            
            return (
                <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors text-xs ${
                        isSelected 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-200 dark:shadow-none' 
                        : isToday 
                            ? 'text-blue-600 font-bold bg-blue-50 dark:bg-blue-900/20' 
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                >
                    {day}
                </button>
            );
        })}
      </div>
    );
  };

  const renderTimePicker = () => {
      if (!value) return null;
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5 min steps

      const handleTimeInput = (type: 'hour' | 'minute', val: string) => {
          let num = parseInt(val);
          if (isNaN(num)) return;
          if (type === 'hour') {
              if (num < 0) num = 0;
              if (num > 23) num = 23;
          } else {
              if (num < 0) num = 0;
              if (num > 59) num = 59;
          }
          handleTimeSelect(type, num);
      };

      return (
          <div className="flex flex-col h-64">
              <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="flex-1">
                      <label className="text-xs text-slate-500 mb-1 block">时</label>
                      <input 
                          type="number" 
                          min={0} 
                          max={23} 
                          value={value.getHours()} 
                          onChange={(e) => handleTimeInput('hour', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-center"
                      />
                  </div>
                  <span className="text-slate-400 mt-5">:</span>
                  <div className="flex-1">
                      <label className="text-xs text-slate-500 mb-1 block">分</label>
                      <input 
                          type="number" 
                          min={0} 
                          max={59} 
                          value={value.getMinutes()} 
                          onChange={(e) => handleTimeInput('minute', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-slate-200 dark:border-slate-600 rounded bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white text-center"
                      />
                  </div>
              </div>
              
              <div className="flex flex-1 gap-2 overflow-hidden min-h-0">
                  <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                      <div className="grid grid-cols-1 gap-1">
                          {hours.map(h => (
                              <button
                                  key={h}
                                  onClick={() => handleTimeSelect('hour', h)}
                                  className={`px-2 py-1.5 text-xs rounded text-center transition-colors ${
                                      value.getHours() === h
                                      ? 'bg-blue-600 text-white'
                                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                  }`}
                              >
                                  {String(h).padStart(2, '0')}
                              </button>
                          ))}
                      </div>
                  </div>
                  <div className="w-px bg-slate-100 dark:bg-slate-700 my-2"></div>
                  <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
                      <div className="grid grid-cols-1 gap-1">
                          {minutes.map(m => (
                              <button
                                  key={m}
                                  onClick={() => handleTimeSelect('minute', m)}
                                  className={`px-2 py-1.5 text-xs rounded text-center transition-colors ${
                                      value.getMinutes() === m
                                      ? 'bg-blue-600 text-white'
                                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                                  }`}
                              >
                                  {String(m).padStart(2, '0')}
                              </button>
                          ))}
                      </div>
                  </div>
              </div>
          </div>
      );
  };

  const formatDisplay = (date: Date) => {
      return `${date.getMonth() + 1}月${date.getDate()}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
        <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={className || `flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                value 
                ? 'bg-blue-50 border-blue-200 text-blue-600 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400' 
                : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
            }`}
        >
            {className ? <CalendarIcon size={14} /> : (
                <>
                    <CalendarIcon size={14} />
                    {value ? formatDisplay(value) : placeholder}
                </>
            )}
        </button>

        {isOpen && createPortal(
            <AnimatePresence>
                <motion.div
                    id="datepicker-portal-content"
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    style={{ 
                        position: 'absolute',
                        top: position.top, 
                        left: position.left,
                        zIndex: 9999 
                    }}
                    className="bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-64 overflow-hidden"
                >
                    <div className="p-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800">
                        {view === 'date' ? (
                            <>
                                <button onClick={handlePrevMonth} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">
                                    <ChevronLeft size={16} className="text-slate-500" />
                                </button>
                                <div className="font-semibold text-slate-700 dark:text-slate-200 text-sm">
                                    {currentMonth.getFullYear()}年 {currentMonth.getMonth() + 1}月
                                </div>
                                <button onClick={handleNextMonth} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors">
                                    <ChevronRight size={16} className="text-slate-500" />
                                </button>
                            </>
                        ) : (
                            <div className="flex items-center gap-2 w-full">
                                <button onClick={() => setView('date')} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors mr-auto">
                                    <ChevronLeft size={16} className="text-slate-500" />
                                </button>
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">选择时间</span>
                                <div className="w-6"></div> 
                            </div>
                        )}
                    </div>

                    <div className="p-3">
                        {view === 'date' ? (
                            <>
                                {renderCalendar()}
                                
                                {value && (
                                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                                        <button 
                                            onClick={() => setView('time')}
                                            className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                                        >
                                            <Clock size={12} />
                                            {String(value.getHours()).padStart(2, '0')}:{String(value.getMinutes()).padStart(2, '0')}
                                        </button>
                                        
                                        <button 
                                            onClick={() => onChange(null)} 
                                            className="text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded transition-colors"
                                        >
                                            清除
                                        </button>
                                    </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                                     <button 
                                        onClick={() => {
                                            const now = new Date();
                                            now.setSeconds(0);
                                            onChange(now);
                                            setIsOpen(false);
                                        }}
                                        className="text-xs py-1.5 px-2 bg-slate-50 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-colors"
                                     >
                                         今天
                                     </button>
                                     <button 
                                        onClick={() => {
                                            const tmr = new Date();
                                            tmr.setDate(tmr.getDate() + 1);
                                            tmr.setHours(9, 0, 0, 0);
                                            onChange(tmr);
                                            setIsOpen(false);
                                        }}
                                        className="text-xs py-1.5 px-2 bg-slate-50 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-colors"
                                     >
                                         明天 9:00
                                     </button>
                                </div>
                            </>
                        ) : (
                            renderTimePicker()
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>,
            document.body
        )}
    </div>
  );
};

export default DatePicker;
